<script setup lang="ts">
  import { IFormItem } from '../utils/interfaces';
  import { computed } from 'vue';
  import { useSubmitForm } from '../hooks/useSubmitForm';
  import { useFormTopics } from '../hooks/useFormTopics';
  import ContactFormPrivacyOption from './ContactFormPrivacyOption.vue';
  import ContactFormContactList from './ContactFormContactList.vue';
  import ContactFormOtherOptions from './ContactFormOtherOptions.vue';

  interface ContactFormProps {
    formItem: IFormItem;
  };

  const props = defineProps<ContactFormProps>();
  const isRTL = computed(() => props.formItem.setFormRtl ? 'rtl' : 'ltr');
  
  const { onSubmitForm } = useSubmitForm();
  const { currentTopic, allTopics, privacyActive, contactListActive, otherTopicActive } = useFormTopics();
</script>

<template>
    <FormKit
    type="form"
    class="contact-form-container"
    :actions="false"
    id="contact-us-form"
    @submit="onSubmitForm"
  >
    <h1 
      class="form-title"
      data-pw-form="form-title"
    >
      {{ formItem.formIntroductionTitle }}
    </h1>
    <div class="form-intro">
      <FormKit  
        type="select"
        name="topic"
        v-model="currentTopic"
        :label="formItem.formTopicIntro"
        :placeholder="formItem.formTopicPlaceholder"
        :options="allTopics"
        validation="required"
        :validation-messages="{ required: formItem.formTopicsErrorMessage }"
        data-pw-form="topic"
      />
    </div>
    <contact-form-privacy-option
      v-if="privacyActive"
      :formItem="formItem"
      :rtlItem="{ useRtl: formItem.setFormRtl, currentDirection: isRTL }"
    />
    <contact-form-contact-list
      v-if="contactListActive"
      :rtlItem="{ useRtl: formItem.setFormRtl, currentDirection: isRTL }"
    />
    <contact-form-other-options
      v-if="otherTopicActive"
      :formItem="formItem"
      :rtlItem="{ useRtl: formItem.setFormRtl, currentDirection: isRTL }"
    />
  </FormKit>
</template>

<style lang="scss" scoped>
  $color-black: #000000;
  $color-grey-dark: #353535;
  $color-white: #ffffff;
  $color-error: #bf1004;

  $font-size-base: 16px;
  $font-size-small: 12px;
  $line-height-base: 24px;
  $line-height-small: 20px;

  $border-radius-base: 8px;
  $border-radius-button: 80px;
  $border-radius-checkbox: 4px;

  $padding-input: 16px;
  $gap-large: 24px;

  :deep(.formkit-label:not(.form-send-copy .formkit-label)),
  :deep(.formkit-message) {
    font-size: $font-size-small;
    font-weight: normal;
    letter-spacing: 0;
    line-height: $line-height-small;
    margin: 8px 0 8px;
    text-decoration: none;
    padding-inline-end: 24px;
    display: block;
  }

  :deep(.formkit-message) {
    color: $color-error;
  }

  :deep(.formkit-input) {
    appearance: none;
    background: $color-white;
    border: 1px solid $color-black;
    border-radius: $border-radius-base;
    color: $color-black;
    font-size: $font-size-base;
    line-height: $line-height-base;
    outline: none;
    padding: $padding-input;
    text-decoration: none;
    display: block;
    width: 100%;
  }

  :deep(select.formkit-input) {
    background-color: $color-white;    
    color: $color-black;       
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 768 768'%3E%3Cpath fill='%23000' d='M531 274.5l45 45-192 192-192-192 45-45 147 147z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right .75rem center;
    background-size: 24px 24px;
    padding-right: 2.5rem;  
  }

  :deep(.formkit-input[type=checkbox]) {
    padding: 0;
    width: 24px;
    height: 24px;
    border-radius: $border-radius-checkbox;
    display: flex;
    align-items: center;
    justify-content: center;

    &:checked {
      background-color: $color-black;

      &::before {
        content: "";
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='white' viewBox='0 0 16 16'><path d='M6.173 11.233 2.5 7.56l1.414-1.414L6.173 8.92l6.086-6.086L13.673 4.25z'/></svg>");
        width: 16px;
        height: 16px;
        display: block;
      }
    }
  }

  :deep(.formkit-input[type=submit]) {
    display: flex;
    font-size: $font-size-base;
    font-weight: bold;
    letter-spacing: 0;
    line-height: $line-height-base;
    align-items: center;
    border-radius: $border-radius-button;
    max-width: 100%;
    padding: 4px 16px;
    text-align: center;
    text-decoration: none;
    background-color: $color-black;
    border: none;
    color: $color-white;
    height: fit-content;
    justify-content: center;
    min-height: 40px;
    width: 100%;
    cursor: pointer;
    
    &:hover {
      background-color: $color-grey-dark;
    }
  }

  :deep(.form-terms-container .formkit-label),
  :deep(.form-terms-container .formkit-input) {
    font-size: $font-size-small;
    line-height: $line-height-small;
  }

  :deep(.form-terms-container input[type=checkbox]:checked) {
    background-color: $color-grey-dark;
  }

  .form-details-two-columns {
    display: flex;
    gap: $gap-large;

    > div {
      flex-basis: 50%;
    }
  }

  .form-intro,
  .formkit-outer {
    margin-top: 32px;
  }

  :deep(.form-send-copy .formkit-wrapper) {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .form-terms-checkbox .formkit-outer {
    margin-top: 0;
  }

  :deep(.form-recaptcha-message a) {
    color: $color-black;
    text-decoration: underline;
  }
</style>